<script lang="ts">
	import Banner from "@components/Banner.svelte";
	import Icon from "@components/common/Icon.svelte";
	import LabelledSelect from "@components/form/LabelledSelect.svelte";
	import Layout from "@components/layout/Layout.svelte";
	import { getTranslate } from "@utils/translate.js";
	import paid from "@core/assets/icons/paid.svg?raw";
	import topped from "@core/assets/icons/topped.svg?raw";
	import type { CompetitionCategoryType } from "@core/schema/CompetitionCategoryType.js";
	import { breedNameMap } from "@core/schema/names/breedNameMap.js";
	import { competitionCategoryTypeNameMap } from "@core/schema/names/competitionCategoryTypeNameMap.js";
	import { countryNameMap } from "@core/schema/names/countryNameMap.js";
	import { createSortApplication } from "@core/schema/utils/sortApplication.js";
	import { countryEmojiMap } from "@core/utils/countryEmojiMap.js";
	import { isBreed } from "@core/utils/isBreed.js";
	import { getGlobalTemplateProps } from "../contexts/getGlobalTemplateProps.js";
	import translations from "./Applications.translations.json" with { type: "json" };
	import type { ApplicationsProps } from "./ApplicationsProps.js";

	const { groupedApplications, applications }: ApplicationsProps = $props();
	const { tenant, competition } = getGlobalTemplateProps();
	const translate = getTranslate(translations);

	let filteredCompetitionCategoryType = $state<CompetitionCategoryType | null>(null);

	const sortApplication = $derived(createSortApplication(filteredCompetitionCategoryType));
	/* eslint-disable svelte/no-at-html-tags */
	/* eslint-disable @typescript-eslint/no-confusing-void-expression */
</script>

{#snippet paidIcon(mode: "unpaid" | "partial" | "paid")}
	<div class="relative group">
		<Icon
			icon={paid}
			class={[
				"w-4",
				{
					"text-[#0EEB0E]": mode === "paid",
					"text-orange-500": mode === "partial",
					"text-red-500": mode === "unpaid",
				},
			]}
		/>
		<div
			class="absolute group-hover:opacity-100 opacity-0 transition-all pointer-events-none top-8 rounded text-sm px-2 py-1 border whitespace-nowrap bg-black border-white z-10"
		>
			{#if mode === "paid"}
				{translate("paid")}
			{:else if mode === "partial"}
				{translate("partiallyPaid")}
			{:else}
				{translate("unpaid")}
			{/if}
		</div>
	</div>
{/snippet}

{#snippet toppedIcon()}
	<div class="relative group">
		<Icon icon={topped} class="w-4" />
		<div
			class="absolute group-hover:opacity-100 opacity-0 transition-all pointer-events-none top-8 rounded text-sm px-2 py-1 border whitespace-nowrap bg-black border-white z-10"
		>
			{translate("topped")}
		</div>
	</div>
{/snippet}

<Layout noFooterMargin>
	{#snippet customMain()}
		<main class="flex flex-col grow">
			{#if competition}
				<div class="grid-container">
					<Banner {competition} />
				</div>
			{/if}
			<div class="bg-black text-white full-width grid-container py-28 relative mt-16">
				<h2 class="mb-8">{translate("headline")}</h2>
				<div class="flex">
					<LabelledSelect bind:value={filteredCompetitionCategoryType}>
						<span class="">{translate("disciplines")}</span>
						{#snippet options()}
							<option value={null}>{translate("all")}</option>
							{#each competition?.categories ?? [] as { type } (type)}
								<option value={type}>{competitionCategoryTypeNameMap[type][tenant.locale]}</option>
							{/each}
						{/snippet}
					</LabelledSelect>
				</div>

				<div class="max-xl:overflow-auto">
					<div
						class={[
							"grid gap-x-12 gap-y-5 mt-6 max-w-full pb-10",
							filteredCompetitionCategoryType !== null
								? "[grid-template-columns:min-content_min-content_repeat(4,auto)]"
								: "[grid-template-columns:min-content_min-content_repeat(5,auto)]",
						]}
					>
						<div class="grid col-span-full grid-cols-subgrid mb-4 font-bold">
							<span class="items-center text-sm">#</span>
							<span class="items-center text-sm">{translate("homeland")}</span>
							<span class="min-w-36 items-center text-sm">{translate("fullName")}</span>

							<span class="min-w-36 items-center text-sm">{translate("dogCallName")}</span>
							<span class="min-w-36 items-center text-sm">{translate("breed")}</span>
							{#if filteredCompetitionCategoryType === null}
								<span class="min-w-52 items-center">{translate("disciplines")}</span>
							{/if}
						</div>

						{#if filteredCompetitionCategoryType !== null}
							{#each applications
								.filter(({ categories }) => categories.some(({ type }) => type === filteredCompetitionCategoryType))
								.toSorted(sortApplication) as application, index (application.id)}
								{@const groupedApplication = groupedApplications[`${application.handlerId}:${application.dogId}`]}
								{@const category = application.categories.find(
									(category) => category.type === filteredCompetitionCategoryType,
								)}
								{#if category && groupedApplication}
									{@const { handler, dog } = groupedApplication}
									<div class="grid grid-cols-subgrid col-span-full text-grey-100">
										<span class="w-fit items-center text-sm flex gap-2">#{index + 1}</span>
										<span class="min-w-36 items-center text-sm flex gap-2"
											><div class="w-6">{@html countryEmojiMap[handler.homeland]}</div>
											{countryNameMap[handler.homeland][tenant.locale]}</span
										>
										<div class="flex gap-2 items-center min-w-36 text-sm">
											{@render paidIcon(application.amountPaid !== undefined ? "paid" : "unpaid")}
											<span>{handler.firstName} {handler.lastName}</span>
											{#if category.topped}
												{@render toppedIcon()}
											{/if}
										</div>
										<span class="min-w-36 flex items-center text-sm">{dog.callName}</span>
										<span class="min-w-36 flex items-center text-sm">
											{isBreed(dog.breed) ? breedNameMap[dog.breed][tenant.locale] : dog.breed}
										</span>
									</div>
								{:else}
									<p class="col-span-full">{translate("nooneApplied")}</p>
								{/if}
							{/each}
						{:else}
							<!-- TODO na seznamu všěech disciplín seřadit rovněž od nejstaršího createdAt -->
							{#each Object.entries(groupedApplications) as [key, { dog, handler, categories, applications }], index (key)}
								{@const paidApplications = applications.filter((application) => application.amountPaid !== undefined)}
								<div class="grid grid-cols-subgrid col-span-full">
									<span class="items-center text-sm flex gap-2 w-fit">#{index + 1}</span>

									<span class="min-w-36 text-sm flex items-center gap-2 whitespace-nowrap"
										><div class="w-6">{@html countryEmojiMap[handler.homeland]}</div>
										{countryNameMap[handler.homeland][tenant.locale]}</span
									>
									<div class="flex gap-2 min-w-36 whitespace-nowrap items-center text-sm">
										{@render paidIcon(
											paidApplications.length === applications.length
												? "paid"
												: paidApplications.length === 0
													? "unpaid"
													: "partial",
										)}

										<span>{handler.firstName} {handler.lastName}</span>
									</div>
									<span class="min-w-36 flex items-center text-sm">{dog.callName}</span>
									<span class="min-w-36 flex items-center text-sm">
										{isBreed(dog.breed) ? breedNameMap[dog.breed][tenant.locale] : dog.breed}
									</span>
									<span class="min-w-36 flex items-center text-sm">
										{categories.map(({ type }) => competitionCategoryTypeNameMap[type][tenant.locale]).join(", ")}
									</span>
								</div>
							{:else}
								<p class="col-span-full">{translate("nooneApplied")}</p>
							{/each}
						{/if}
					</div>
				</div>
			</div>
		</main>
	{/snippet}
</Layout>
